import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import heroImg from "@/assets/mision-aventura.jpg";
import { useT, useMedia } from "@/lib/i18n";

export const Route = createFileRoute("/about/mision")({
  component: MissionPage,
  head: () => ({
    meta: [
      { title: "Misión y Valores — Bosque Suspendido" },
      { name: "description", content: "Nuestra misión: aventura entre pinos, naturaleza viva y seguridad para que cada visita a Bosque Suspendido sea inolvidable." },
      { property: "og:title", content: "Misión y Valores — Bosque Suspendido" },
      { property: "og:description", content: "Aventura entre pinos, respeto por la naturaleza y seguridad en cada salto." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/about/mision" },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/about/mision" }],
  }),
});

function MissionPage() {
  const { t } = useT();
  const hero = useMedia("about.hero", heroImg, "Aventura entre los pinos de Bosque Suspendido");

  return (
    <Layout>
      <section className="border-y border-gold/15 bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {t("Nuestra misión", "A nossa missão", "Our Mission")}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl mt-4">
              {t(
                "Que vivas la naturaleza desde las alturas, con el corazón a mil y los pies (casi) en el suelo.",
                "Que vivas a natureza desde as alturas, com o coração a mil e os pés (quase) no chão.",
                "Experience nature from above, heart racing and feet (almost) on the ground.",
              )}
            </h1>
            <div className="gold-divider my-6 max-w-[100px]" />
            <p className="text-muted-foreground leading-relaxed">
              {t(
                "En Bosque Suspendido creemos que la mejor aventura es la que se vive entre pinos: tirolinas, puentes colgantes y retos en plena naturaleza, diseñados para todas las edades y con la seguridad como prioridad absoluta.",
                "No Bosque Suspendido acreditamos que a melhor aventura é a que se vive entre pinheiros: slides, pontes suspensas e desafios em plena natureza, pensados para todas as idades e com a segurança como prioridade absoluta.",
                "At Bosque Suspendido we believe the best adventure happens among the pines: zip lines, hanging bridges and challenges in the heart of nature, designed for all ages with safety as our top priority.",
              )}
            </p>
            <ul className="mt-8 space-y-4">
              {[
                t(
                  "Seguridad ante todo. Equipos homologados, línea de vida continua y monitores titulados en cada circuito.",
                  "Segurança acima de tudo. Equipamentos homologados, linha de vida contínua e monitores certificados em cada circuito.",
                  "Safety first. Certified gear, continuous lifeline and qualified instructors on every course.",
                ),
                t(
                  "Naturaleza viva. Cuidamos el pinar que nos acoge: instalaciones respetuosas que no dañan los árboles.",
                  "Natureza viva. Cuidamos do pinhal que nos acolhe: instalações respeitosas que não danificam as árvores.",
                  "Living nature. We protect the pine forest that hosts us: tree-friendly installations that cause no harm.",
                ),
                t(
                  "Aventura para todos. Circuitos infantiles, familiares y deportivos: cada uno encuentra su reto.",
                  "Aventura para todos. Circuitos infantis, familiares e desportivos: cada um encontra o seu desafio.",
                  "Adventure for everyone. Kids, family and sport circuits: there is a challenge for each visitor.",
                ),
                t(
                  "Aprender jugando. Programas escolares y temáticos donde la diversión y la educación van de la mano.",
                  "Aprender a brincar. Programas escolares e temáticos onde a diversão e a educação andam de mãos dadas.",
                  "Learning through play. School and themed programmes where fun and education go hand in hand.",
                ),
              ].map((v) => (
                <li key={v} className="flex gap-4">
                  <span className="text-gold font-serif text-xl leading-none">✦</span>
                  <span className="text-foreground/80">{v}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="mt-10 inline-flex items-center gap-3 wood-btn px-7 py-3.5 text-xs tracking-[0.3em] uppercase">
              {t("Reserva tu aventura", "Reserva a tua aventura", "Book your adventure")}
            </Link>
          </div>
          <div className="relative">
            <img src={hero.url} alt={hero.alt} width={1024} height={1280} loading="lazy" className="w-full h-[560px] object-cover rounded-xl" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
